import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from './stores/authStore'
import { ThemeProvider } from './contexts/ThemeContext'
import { useTokenRefresh } from './hooks/useTokenRefresh'

// Pages
import SignIn from './pages/auth/SignIn'
import SignUp from './pages/auth/SignUp'
import Dashboard from './pages/Dashboard'
import ChatInterface from './pages/ChatInterface'
import Customers from './pages/Customers'
import Analytics from './pages/Analytics'
import Settings from './pages/Settings'

// Components
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'

// Signed-in agents landing on /signin or /signup go back to the page they originally asked for
function RedirectIfAuthenticated({ children }) {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  if (isAuthenticated) {
    const from = location.state?.from
    const target = from ? `${from.pathname}${from.search || ''}${from.hash || ''}` : '/dashboard'
    return <Navigate to={target} replace />
  }
  return children
}

function AppContent() {
  // Initialize token refresh logic
  useTokenRefresh()

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/signin" 
            element={<RedirectIfAuthenticated><SignIn /></RedirectIfAuthenticated>}
          />
          <Route 
            path="/signup" 
            element={<RedirectIfAuthenticated><SignUp /></RedirectIfAuthenticated>}
          />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
            <Route path="/chat" element={<Layout><ChatInterface /></Layout>} />
            <Route path="/chat/:sessionId" element={<Layout><ChatInterface /></Layout>} />
            <Route path="/customers" element={<Layout><Customers /></Layout>} />
            <Route path="/analytics" element={<Layout><Analytics /></Layout>} />
            <Route path="/settings" element={<Layout><Settings /></Layout>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        {/* Global Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#374151',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb',
            },
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </Router>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  )
}

export default App
